<template>
  <div class="modal-card select-campaign-image-modal">
    <header class="modal-card-head">
      <p class="modal-card-title">{{ title }}</p>
      <button
        type="button"
        class="delete"
        @click="$emit('close')" />
    </header>
    <section class="modal-card-body">
      <b-tabs v-model="activeTab" type="is-boxed">
        <b-tab-item label="Upload File">
          <div v-if="!imageSrc" class="upload-area" @click="openFileInput" @dragover.prevent @drop.prevent="onDrop">
            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              class="is-hidden"
              @change="onFileSelected" />
            <b-icon icon="image-plus" size="is-large" custom-class="has-text-grey-light" />
            <p class="upload-text">Drop an image here or click to upload</p>
          </div>
          <div v-else class="cropper-area">
            <div :class="['cropper-wrapper', { 'transparency-bg': supportTransparency }]">
              <Cropper
                ref="cropper"
                class="cropper"
                :src="imageSrc"
                :stencil-props="stencilProps"
                :resize-image="{ wheel: { ratio: 0.1 }, touch: true }"
                :move-image="true"
                image-restriction="none"
                :default-size="defaultCropSize"
              />
            </div>
            <div class="toolbar">
              <b-button size="is-medium" icon-left="magnify-plus-outline" @click="zoomIn" />
              <b-button size="is-medium" icon-left="magnify-minus-outline" @click="zoomOut" />
              <span class="toolbar-divider" />
              <b-button size="is-medium" icon-left="rotate-left" @click="rotateLeft" />
              <b-button size="is-medium" icon-left="rotate-right" @click="rotateRight" />
              <span class="toolbar-divider" />
              <b-button size="is-medium" icon-left="flip-horizontal" @click="flipH" />
              <b-button size="is-medium" icon-left="flip-vertical" @click="flipV" />
            </div>
            <p class="help mt-1">Drag to reposition, scroll to zoom</p>
            <p v-if="supportTransparency" class="help has-text-info">
              <b-icon icon="information" size="is-medium" /> PNG transparency is preserved
            </p>
          </div>
        </b-tab-item>
        <b-tab-item label="From URL">
          <b-field label="Image URL">
            <b-input
              v-model="imageUrl"
              placeholder="https://example.com/image.png"
              type="url"
              expanded
            />
          </b-field>
          <b-field v-if="imageUrl" label="Preview">
            <figure :class="['image', 'preview-container', { 'transparency-bg': supportTransparency }]">
              <img :src="imageUrl" @error="urlImageError = true" @load="urlImageError = false" />
            </figure>
          </b-field>
          <b-message v-if="urlImageError" type="is-danger" size="is-medium">
            Could not load image from URL
          </b-message>
          <b-message type="is-info" size="is-medium">
            The image will be downloaded and stored on our server for safety.
          </b-message>
        </b-tab-item>
      </b-tabs>
    </section>
    <footer class="modal-card-foot">
      <b-button
        label="Remove"
        @click="resetImage" />
      <b-button
        label="Save"
        type="is-primary"
        :loading="isUploading"
        :disabled="isSaveDisabled"
        @click="useImage" />
    </footer>
  </div>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'

export default {
  name: 'SelectCampaignImageModal',
  components: {
    Cropper
  },
  props: {
    title: {
      type: String,
      default: 'Select Image'
    },
    aspectRatio: {
      type: String,
      default: 'flexible' // 'square', '16:9', '4:3', 'flexible'
    },
    supportTransparency: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      imageSrc: null,
      activeTab: 0,
      imageUrl: '',
      urlImageError: false,
      isUploading: false,
      flippedH: false,
      flippedV: false
    }
  },
  computed: {
    stencilProps() {
      switch (this.aspectRatio) {
        case 'square': return { aspectRatio: 1 }
        case '16:9': return { aspectRatio: 16 / 9 }
        case '4:3': return { aspectRatio: 4 / 3 }
        default: return {}
      }
    },
    isSaveDisabled() {
      if (this.activeTab === 0) {
        return !this.imageSrc
      } else {
        return !this.imageUrl || this.urlImageError
      }
    },
    outputMimeType() {
      return this.supportTransparency ? 'image/png' : 'image/jpeg'
    },
    outputExtension() {
      return this.supportTransparency ? 'png' : 'jpg'
    }
  },
  methods: {
    defaultCropSize({ imageSize }) {
      return {
        width: Math.min(imageSize.width, imageSize.height) * 0.9,
        height: Math.min(imageSize.width, imageSize.height) * 0.9
      }
    },
    openFileInput() {
      this.$refs.fileInput.click()
    },
    onFileSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.loadFile(file)
      }
    },
    onDrop(event) {
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.loadFile(file)
      }
    },
    loadFile(file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.imageSrc = e.target.result
      }
      reader.readAsDataURL(file)
    },
    resetImage() {
      if (this.activeTab === 0) {
        this.imageSrc = null
        this.flippedH = false
        this.flippedV = false
      } else {
        this.imageUrl = ''
        this.urlImageError = false
      }
    },
    zoomIn() {
      this.$refs.cropper.zoom(1.2)
    },
    zoomOut() {
      this.$refs.cropper.zoom(0.8)
    },
    rotateLeft() {
      this.$refs.cropper.rotate(-90)
    },
    rotateRight() {
      this.$refs.cropper.rotate(90)
    },
    flipH() {
      this.flippedH = !this.flippedH
      this.$refs.cropper.flip(true, false)
    },
    flipV() {
      this.flippedV = !this.flippedV
      this.$refs.cropper.flip(false, true)
    },
    async useImage() {
      this.isUploading = true

      if (this.activeTab === 0) {
        const { canvas } = this.$refs.cropper.getResult()
        if (canvas) {
          canvas.toBlob(
            blob => {
              this.$emit('image-selected', {
                blob,
                type: 'file',
                extension: this.outputExtension
              })
              this.$emit('close')
            },
            this.outputMimeType,
            this.supportTransparency ? 1 : 0.9
          )
        }
      } else {
        try {
          const response = await fetch(this.imageUrl)
          const blob = await response.blob()
          let extension = this.outputExtension
          if (blob.type === 'image/png' || this.imageUrl.toLowerCase().endsWith('.png')) {
            extension = 'png'
          }
          this.$emit('image-selected', {
            blob,
            type: 'url',
            originalUrl: this.imageUrl,
            extension
          })
          this.$emit('close')
        } catch (error) {
          this.$buefy.toast.open({ type: 'is-danger', message: 'Failed to fetch image from URL' })
          this.isUploading = false
        }
      }
    }
  }
}
</script>

<style scoped>
.upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  border: 2px dashed var(--border-medium, #cbd5e1);
  border-radius: var(--radius-sm, 8px);
  background: var(--bg-tertiary, #f1f3f9);
  cursor: pointer;
  transition: border-color var(--transition-fast, 0.15s ease),
              background var(--transition-fast, 0.15s ease);
}
.upload-area:hover {
  border-color: var(--feeds-green, #00857a);
  background: var(--feeds-green-light, #e6f5f3);
}

.upload-text {
  margin-top: 0.75rem;
  color: var(--text-secondary, #64748b);
  font-size: 0.95rem;
}

.cropper-area {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cropper-wrapper {
  border-radius: var(--radius-sm, 8px);
  overflow: hidden;
}

.cropper {
  min-height: 400px;
  max-height: 72vh;
  background: #1a1a1a;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.toolbar-divider {
  width: 1px;
  height: 20px;
  background: var(--border-light, #e2e8f0);
  margin: 0 0.25rem;
}

.preview-container {
  max-width: 100%;
  max-height: 300px;
  overflow: hidden;
  border-radius: 8px;
  background: #f5f5f5;
}
.preview-container img {
  width: 100%;
  height: auto;
  object-fit: contain;
}

/* Checkerboard pattern for transparency preview */
.transparency-bg {
  background-image:
    linear-gradient(45deg, #ccc 25%, transparent 25%),
    linear-gradient(-45deg, #ccc 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #ccc 75%),
    linear-gradient(-45deg, transparent 75%, #ccc 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  background-color: #fff;
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .select-campaign-image-modal {
    width: 100vw;
    max-width: 100vw;
    margin: 0;
    border-radius: 0;
    height: 100vh;
    max-height: 100vh;
  }
  .select-campaign-image-modal .modal-card-body {
    flex: 1;
    overflow-y: auto;
  }
  .cropper {
    min-height: 250px;
    max-height: calc(100vh - 220px);
  }
  .upload-area {
    min-height: calc(100vh - 220px);
  }
}
</style>

<style>
/* Unscoped — targets the Buefy modal wrapper that sits outside this component */
.select-image-modal-wrapper .animation-content,
.select-image-modal-wrapper .animation-content .modal-card {
  width: 1000px !important;
  max-width: 95vw !important;
}
@media (max-width: 768px) {
  .select-image-modal-wrapper .animation-content,
  .select-image-modal-wrapper .animation-content .modal-card {
    width: 100vw !important;
    max-width: 100vw !important;
  }
}
</style>
